import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Seo } from "../components/layout/Seo";
import { Section } from "../components/ui/Section";
import { Button } from "../components/ui/Button";
import { StatusBadge } from "../components/ui/StatusBadge";
import { programs } from "../content/programs";
import { isLocale, localizedPath } from "../lib/routes";

export function ProgramDetail() {
  const { slug, locale } = useParams();
  const { t } = useTranslation();
  const activeLocale = isLocale(locale) ? locale : "en";
  const program = programs.find((item) => item.slug === slug);

  if (!program) {
    return <Section title="Program not found" className="pt-[calc(var(--header-height)+72px)]"><Button to={localizedPath(activeLocale, "programs")}>Programs</Button></Section>;
  }

  if (program.slug === "green-wine-moldova") {
    return <GreenWineProgram />;
  }

  return (
    <>
      <Seo title={program.title[activeLocale]} description={program.summary[activeLocale]} />
      <Section className="pt-[calc(var(--header-height)+72px)]">
        <Link className="mb-8 inline-flex items-center gap-2 text-sm font-bold text-brand-blue" to={localizedPath(activeLocale, "programs")}><ArrowLeft className="rtl-flip h-4 w-4" /> {t("programs.title")}</Link>
        <StatusBadge status={program.status} />
        <h1 className="mt-6 max-w-4xl text-5xl font-extrabold leading-tight">{program.title[activeLocale]}</h1>
        <p className="mt-6 max-w-3xl text-xl leading-8 text-ink-secondary">{program.summary[activeLocale]}</p>
      </Section>
      <Section className="bg-bg-secondary">
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <h2 className="text-3xl font-bold">Focus</h2>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {program.focus[activeLocale].map((item) => <div key={item} className="border border-line bg-surface p-5 font-bold">{item}</div>)}
          </div>
        </div>
      </Section>
      <Section title="Program architecture">
        <div className="max-w-3xl space-y-5 text-lg leading-8 text-ink-secondary">
          {program.body[activeLocale].map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        </div>
      </Section>
    </>
  );
}

function GreenWineProgram() {
  const { locale } = useParams();
  const { t } = useTranslation();
  const activeLocale = isLocale(locale) ? locale : "en";
  const journey = ["Vineyard", "Water & Climate", "Winery Energy", "Storage & Efficiency", "Sustainability Evidence", "European Market"];

  return (
    <>
      <Seo title="Green Wine Moldova" description={t("programs.greenIntro")} />
      <section className="bg-brand-burgundySoft pt-[calc(var(--header-height)+64px)]">
        <div className="container-grid pb-16">
          <p className="eyebrow mb-5 text-brand-burgundy">FLAGSHIP PROGRAM</p>
          <h1 className="text-balance text-5xl font-extrabold leading-tight text-brand-burgundy md:text-7xl">Green Wine Moldova</h1>
          <p className="mt-6 max-w-2xl text-2xl font-semibold">{t("programs.greenIntro")}</p>
        </div>
      </section>
      <Section title="From vineyard to market">
        <div className="grid gap-3 md:grid-cols-6">
          {journey.map((item) => <div key={item} className="border border-brand-burgundy/20 bg-surface p-5 font-bold text-brand-burgundy">{item}</div>)}
        </div>
      </Section>
      <Section title={t("programs.programNotProduct")} className="bg-bg-secondary">
        <div className="max-w-3xl space-y-5 text-lg leading-8 text-ink-secondary">
          <p>Green Wine Moldova can bring together producers, technology providers, researchers, financing institutions and sector organizations around measurable sustainability.</p>
          <p>No organization is presented as an official partner until that status is confirmed.</p>
        </div>
        <Button className="mt-8" to={localizedPath(activeLocale, "contact?flow=green-wine")}>{t("programs.discuss")}</Button>
      </Section>
    </>
  );
}
